import {
  EVENT_LISTENER_METHODS,
  eventBus,
  LAYOUT,
  miliSecondsToMMSS,
  setLayout,
  toggleParticipantListVisible,
  useBaseStore,
  useConfigStore,
  useLayout,
  useSessionTimer,
  useTranslation,
  type Layout,
} from '@cometchat/calls-sdk-core';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import Icon from '../icon/Icon.web';
import ChatButton from './ChatButton.web';
import './style.css';

const SessionTimer = () => {
  const conferenceElapsedTime = useSessionTimer();
  const hideSessionTimer = useConfigStore(state => state.hideSessionTimer);

  if (hideSessionTimer || conferenceElapsedTime < 0) {
    return null;
  }

  return (
    <div
      className="cometchat-calls-header-session-timer"
      data-testid="cometchat-session-timer">
      {miliSecondsToMMSS(conferenceElapsedTime)}
    </div>
  );
};

const LayoutDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [renderDropdown, setRenderDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentLayout = useLayout();
  const { t } = useTranslation();

  const layouts = [
    { id: LAYOUT.TILE, name: t('header.layouts.tileView'), icon: 'tile-fill' },
    {
      id: LAYOUT.SIDEBAR,
      name: t('header.layouts.sideLayout'),
      icon: 'sidebar',
    },
    {
      id: LAYOUT.SPOTLIGHT,
      name: t('header.layouts.spotlight'),
      icon: 'spotlight',
    },
  ] as const;

  function setDropDownOpen(open: boolean) {
    setIsOpen(open);
    if (open) {
      setRenderDropdown(open);
    } else {
      setTimeout(() => {
        setRenderDropdown(open);
      }, 150);
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropDownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLayoutSelect = (layout: Layout) => {
    setLayout(layout);
    setDropDownOpen(false);
  };

  return (
    <div
      className="cometchat-calls-header-layout-dropdown"
      ref={dropdownRef}
      data-testid="cometchat-calls-header-layout-dropdown">
      <button
        className={clsx(
          'cometchat-calls-header-action-button',
          isOpen && 'cometchat-calls-header-action-button--active',
        )}
        onClick={() => {
          eventBus.publish({
            type: EVENT_LISTENER_METHODS.ButtonClickListener
              .onChangeLayoutButtonClicked,
          });
          setDropDownOpen(!isOpen);
        }}>
        <Icon name="tile-fill" fill={'var(--cometchat-calls-static-white)'} />
      </button>

      {renderDropdown && (
        <div
          className={clsx('cometchat-calls-header-dropdown-menu', {
            'cometchat-calls-header-dropdown-menu--opening': isOpen,
            'cometchat-calls-header-dropdown-menu--closing': !isOpen,
          })}>
          {layouts.map(layout => (
            <button
              key={layout.id}
              className={clsx(
                'cometchat-calls-header-dropdown-item',
                currentLayout === layout.id &&
                  'cometchat-calls-header-action-button--active',
              )}
              onClick={() => handleLayoutSelect(layout.id)}>
              <Icon
                name={layout.icon}
                size={20}
                fill={'var(--cometchat-calls-neutral-color-600)'}
              />
              <span className="cometchat-calls-header-dropdown-item-name">
                {layout.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Header: React.FC = () => {
  const participantListVisible = useBaseStore(
    state => state.participantListVisible,
  );
  const hideParticipantListButton = useConfigStore(
    state => state.hideParticipantListButton,
  );
  const hideChangeLayoutButton = useConfigStore(
    state => state.hideChangeLayoutButton,
  );
  const title = useConfigStore(state => state.title);
  const hideHeaderPanel = useConfigStore(state => state.hideHeaderPanel);

  if (hideHeaderPanel) {
    return null;
  }

  return (
    <div className="cometchat-calls-header-container">
      {/* Left Section: Meeting Info */}
      <div className="cometchat-calls-header-session-info">
        <div className="cometchat-calls-header-title">{title}</div>
        <SessionTimer />
      </div>

      {/* Right Section: Icon Controls */}
      <div className="cometchat-calls-header-actions">
        {!hideParticipantListButton && (
          <button
            data-testid="cometchat-participant-list-button"
            className={clsx(
              'cometchat-calls-header-action-button',
              participantListVisible &&
                'cometchat-calls-header-action-button--active',
            )}
            onClick={() => {
              toggleParticipantListVisible();
              eventBus.publish({
                type: EVENT_LISTENER_METHODS.ButtonClickListener
                  .onParticipantListButtonClicked,
              });
            }}>
            <Icon
              name="participants"
              fill={'var(--cometchat-calls-static-white)'}
            />
          </button>
        )}
        <ChatButton />
        {!hideChangeLayoutButton && <LayoutDropdown />}
      </div>
    </div>
  );
};

export default Header;
